import firebase from "firebase";
import whoami from "./selectors/whoami";
import LocalStorage from "./util/LocalStorage";
import STATUS from "./util/STATUS";

export const NEW_VERSION_AVAILABLE = "NEW_VERSION_AVAILABLE";

export const newVersionAvailable = () => ({
    type: NEW_VERSION_AVAILABLE,
});

export const EMULATE_PLAYER = "EMULATE_PLAYER";

export const emulatePlayer = (yesOrNo) => ({
    type: EMULATE_PLAYER,
    emulate: yesOrNo,
});

export const BOOTSTRAP = "BOOTSTRAP";

export const bootstrap = () => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname.endsWith('.local');
    return {
        type: BOOTSTRAP,
        path: window.location.pathname,
        isLocalhost,
        messagingToken: LocalStorage.getMessagingToken(),
    };
};

/* eslint-disable no-console */
export const refreshMessagingToken = () =>
    dispatch => {
        const messaging = firebase.messaging();
        messaging.requestPermission()
            .then(() =>
                messaging.getToken()
                    .then(currentToken => {
                        if (currentToken) {
                            console.log("got token:", currentToken);
                            dispatch(updateMessagingToken(currentToken));
                        } else {
                            console.log("No Instance ID token available. Request permission to generate one.");
                            dispatch(revokeMessagingToken());
                        }
                    })
                    .catch(err => {
                        console.log("Unable to retrieve refreshed token ", err);
                        dispatch(revokeMessagingToken());
                    }))
            .catch(err => console.log("Unable to get permission to notify.", err));
    };
/* eslint-enable no-console */

export const CLEAR_MESSAGING_TOKEN = "CLEAR_MESSAGING_TOKEN";

export const revokeMessagingToken = () =>
    (dispatch, getState, getFirebase) => {
        const state = getState();
        const me = whoami(state);
        const db = getFirebase().firestore();
        const userRef = db.collection("users").doc(me);
        db.runTransaction(tx =>
            tx.get(userRef).then(u => {
                const tokens = u.data().tokens || [];
                const i = tokens.indexOf(state.messagingToken);
                if (i >= 0) {
                    tokens.splice(i, 1);
                    tx.update(userRef, {
                        tokens: tokens.length > 0 ? tokens : firebase.firestore.FieldValue.delete(),
                    });
                }
            }),
        )
            .then(() => {
                LocalStorage.clearMessagingToken();
                dispatch({
                    type: CLEAR_MESSAGING_TOKEN,
                });
            })
            .catch(err => console.warn("token tx", "error", err));
    };

export const SAVE_MESSAGING_TOKEN = "SAVE_MESSAGING_TOKEN";

export const updateMessagingToken = token =>
    (dispatch, getState, getFirebase) => {
        const me = whoami(getState());
        const db = getFirebase().firestore();
        const userRef = db.collection("users").doc(me);
        db.runTransaction(tx =>
            tx.get(userRef).then(u => {
                const tokens = u.data().tokens || [];
                if (tokens.indexOf(token) < 0) {
                    tokens.push(token);
                    tx.update(userRef, { tokens: tokens });
                }
                return token;
            }),
        )
            .then(token => {
                LocalStorage.setMessagingToken(token);
                dispatch({
                    type: SAVE_MESSAGING_TOKEN,
                    token,
                });
            })
            .catch(err => console.warn("token tx", "error", err));
    };

export const TOGGLE_ACTIVE_CONTEXT = "TOGGLE_ACTIVE_CONTEXT";

export const toggleActiveContext = id => ({
    type: TOGGLE_ACTIVE_CONTEXT,
    id: id,
});

export const createContext = () =>
    (dispatch, getState, getFirebase) => {
        const me = whoami(getState());
        const db = getFirebase().firestore();
        db.collection("contexts")
            .add({
                createdAt: new Date(),
                creator: db.collection("users").doc(me),
                status: STATUS.DRAFT,
                title: "New Context #" + (Date.now() % 100),
                description: "Fill me out...",
            })
            .then(ref =>
                // it'd be great if this enter edit mode too, but that's not in the store
                dispatch(toggleActiveContext(ref.id))
            )
            .catch(pCatch("error creating context"));
    };

export const updateContext = (cid, data) =>
    (dispatch, getState, getFirebase) => {
        const me = whoami(getState());
        const db = getFirebase().firestore();
        const userRef = db.collection("users").doc(me);
        let tail = Promise.resolve();
        if (data.image) {
            const file = data.image;
            delete data.image;
            tail = tail.then(() =>
                promiseMedia(file, userRef)
                    .then(ref => data.media = ref),
            );
        }
        if (!data.regions || data.regions.length === 0) {
            data.regions = firebase.firestore.FieldValue.delete();
        }
        return tail.then(() =>
            db.collection("contexts")
                .doc(cid)
                .update(data),
        )
            .catch(pCatch("error updating context", data));
    };

const pCatch = (message, ...moar) => e => {
    console.warn(message, e, ...moar);
    return Promise.reject(e);
};

const promiseMedia = (file, userRef) =>
    firebase.firestore()
        .collection("media")
        .add({
            createdAt: new Date(),
            creator: userRef,
            name: file.name,
        })
        .then(ref =>
            firebase.storage()
                .ref(`${ref.id}/${file.name}`)
                .put(file)
                .then(res => {
                    const md = res.metadata;
                    return ref.update({
                        size: md.size,
                        contentType: md.contentType,
                        fullPath: md.fullPath,
                        downloadURL: md.downloadURLs[0],
                    })
                        .then(() => ref)
                        .catch(pCatch("error recording metadata"));
                })
                .catch(pCatch("error saving file")),
        )
        .catch(pCatch("error creating media"));


export const createSubmission = (cid, region, data) =>
    (dispatch, getState, getFirebase) => {
        const me = whoami(getState());
        const db = getFirebase().firestore();
        const userRef = db.collection("users").doc(me);
        const ctxRef = db.collection("contexts").doc(cid);
        let tail = Promise.resolve();
        if (data.counters) {
            tail = tail.then(() =>
                db.runTransaction(tx =>
                    tx.get(ctxRef).then(cd => {
                        const counters = cd.data().counters || 0;
                        tx.update(ctxRef, {
                            counters: counters + data.counters
                        });
                    }),
                )
                    .catch(pCatch("error applying counter delta")),
            );
        }
        if (data.file) {
            const file = data.file;
            delete data.file;
            tail = tail.then(() =>
                promiseMedia(file, userRef)
                    .then(ref => data.media = ref),
            );
        }
        return tail.then(() =>
            db.collection("contexts")
                .doc(cid)
                .collection("submissions")
                .add({
                    ...data,
                    createdAt: new Date(),
                    creator: userRef,
                    region,
                }),
        )
            .catch(pCatch("unknown error"));
    };

export const FILTER_CONTEXTS = "FILTER_CONTEXTS";

export const filterContexts = status => ({
    type: FILTER_CONTEXTS,
    status: status,
});



// WEBPACK FOOTER //
// ./src/actions.js